import clsx from "clsx";
import merge from "lodash/merge";
import { FunctionComponent, JSX } from "react";

import { type ContentCardArticle } from "@ui/components/content-cards/content-cards.types";
import ProminentContentCard from "@ui/components/content-cards/ProminentContentCard";
import { type Pattern } from "@ui/components/patterns/patterns.types";
import * as standardStyles from "@ui/components/patterns/ThreeGridPattern/ThreeGridPattern.css";
import { useComponentConfig } from "@ui/context/component-config-context";
import { sharedStyles } from "@ui/shared-styles";
import { getContexts } from "@ui/utils/patterns";

import * as styles from "./ThreeGridBigPattern.css";

export interface Props extends Pattern {
  componentContext?: string;
  centerText?: boolean;
}

const ThreeGridBigPattern: FunctionComponent<Props> = (props) => {
  const threeGridBigPatternConfig = useComponentConfig(
    "threeGridBigPattern",
    props.componentContext || "",
  );

  const {
    cardsData,
    gaComponentName = "three_grid_big_pattern",
    mobileCardContext = "threeGridPattern",
    desktopCardContext,
    showGridBorders = false,
    mobileCardClassName,
    desktopCardClassName,
    desktopContainerClassName,
    mobileContainerClassName,
  } = merge({}, threeGridBigPatternConfig, props);

  const { mobileContext, desktopContext } = getContexts(
    cardsData.length,
    mobileCardContext,
    desktopCardContext,
  );

  const getDesktopCard = (entry: ContentCardArticle, idx: number) => {
    return (
      <div
        className={clsx(showGridBorders && standardStyles.gridBorders)}
        key={entry.id}
      >
        <ProminentContentCard
          {...entry}
          componentContext={desktopContext[idx]}
          gaComponentName={gaComponentName}
          imageTreatment={idx === 0 || idx % 2 === 0 ? "two" : "three"}
          imageOverlay={idx === 0 || idx % 2 === 0 ? "two" : "three"}
          className={desktopCardClassName}
        />
      </div>
    );
  };

  const getMobileCard = (entry: ContentCardArticle, idx: number) => {
    return (
      <ProminentContentCard
        {...entry}
        key={entry.id}
        componentContext={mobileContext[idx]}
        gaComponentName={gaComponentName}
        extraContentBlockStyles={clsx(props.centerText && styles.centerText)}
        imageTreatment={idx === 0 || idx % 2 === 0 ? "two" : "three"}
        imageOverlay={idx === 0 || idx % 2 === 0 ? "two" : "three"}
        className={clsx(
          idx !== cardsData.length - 1 &&
            sharedStyles.layout.standardSpacing.medium,
          mobileCardClassName,
        )}
      />
    );
  };

  const desktopCards: Array<JSX.Element> = [];
  const mobileCards: Array<JSX.Element> = [];

  cardsData.forEach((entry, idx) => {
    desktopCards.push(getDesktopCard(entry, idx));
    mobileCards.push(getMobileCard(entry, idx));
  });

  return (
    <div>
      <div className={clsx(styles.desktopContainer, desktopContainerClassName)}>
        {desktopCards}
      </div>
      <div
        className={clsx(
          standardStyles.mobileContainer,
          mobileContainerClassName,
        )}
      >
        {mobileCards}
      </div>
    </div>
  );
};

export default ThreeGridBigPattern;
